import React, { useEffect, useState } from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, Tooltip,
} from 'recharts';
import { Loader2, MessageSquareQuote, BarChart3, Search, ShieldAlert } from 'lucide-react';
import { atlasApi, AtlasUser, AvaliacaoCiclo, AvaliacaoResultado } from '../shared/atlasApi';
import { inputCls, labelCls } from '../shared/ListaEditavel';

interface Props {
  user: AtlasUser;
  // Quando fornecidos, abre direto o resultado (ex.: a partir da ficha). Caso contrário, mostra seletores.
  cicloId?: string;
  funccodi?: number;
  // Esconde os seletores (modo embutido na ficha).
  fixo?: boolean;
}

const TIPO_LABEL: Record<string, string> = {
  auto: 'Autoavaliação', gestor: 'Gestor', pares: 'Pares', liderados: 'Liderados',
};

const RADAR_SERIES: { key: 'auto' | 'gestor' | 'pares' | 'liderados'; label: string; cor: string }[] = [
  { key: 'auto', label: 'Autoavaliação', cor: '#8b5cf6' },
  { key: 'gestor', label: 'Gestor', cor: '#3b82f6' },
  { key: 'pares', label: 'Pares', cor: '#f59e0b' },
  { key: 'liderados', label: 'Liderados', cor: '#10b981' },
];

const Resultado: React.FC<Props> = ({ user, cicloId, funccodi, fixo }) => {
  const [ciclos, setCiclos] = useState<AvaliacaoCiclo[]>([]);
  const [cicloSel, setCicloSel] = useState<string>(cicloId || '');
  const [funcSel, setFuncSel] = useState<string>(funccodi ? String(funccodi) : '');
  const [funcAtivo, setFuncAtivo] = useState<number | null>(funccodi || null);

  const [res, setRes] = useState<AvaliacaoResultado | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Carrega ciclos encerrados para o seletor (apenas quando não está fixo).
  useEffect(() => {
    if (fixo) return;
    atlasApi.getCiclosAval(user, { status: 'encerrado' })
      .then(r => setCiclos(r.itens || []))
      .catch(() => {});
  }, [user, fixo]);

  const carregarResultado = (cid: string, fc: number) => {
    if (!cid || !fc) return;
    setLoading(true);
    setErro(null);
    setRes(null);
    atlasApi.getResultadoAval(user, cid, fc)
      .then(r => setRes(r))
      .catch(e => setErro(e.message || 'Erro ao carregar resultado'))
      .finally(() => setLoading(false));
  };

  // Auto-carrega quando props vêm prontas (modo ficha) ou quando o usuário busca.
  useEffect(() => {
    if (cicloId && funccodi) carregarResultado(cicloId, funccodi);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cicloId, funccodi, user]);

  const buscar = () => {
    const fc = parseInt(funcSel, 10);
    if (!cicloSel) { setErro('Selecione um ciclo.'); return; }
    if (isNaN(fc)) { setErro('Informe um funccodi válido.'); return; }
    setFuncAtivo(fc);
    carregarResultado(cicloSel, fc);
  };

  const radarData = (res?.radar || []).map(r => ({
    competencia: r.competencia,
    auto: r.auto ?? 0,
    gestor: r.gestor ?? 0,
    pares: r.pares ?? 0,
    liderados: r.liderados ?? 0,
  }));

  return (
    <div className="space-y-4 max-w-4xl">
      {!fixo && (
        <div className="bg-white rounded-2xl border border-gray-200 p-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-3"><BarChart3 className="w-5 h-5 text-purple-500" /> Resultados de avaliação</h2>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
            <div>
              <label className={labelCls}>Ciclo encerrado</label>
              <select className={inputCls} value={cicloSel} onChange={e => setCicloSel(e.target.value)}>
                <option value="">Selecione...</option>
                {ciclos.map(c => <option key={c._id} value={c._id}>{c.nome}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Funccodi do avaliado</label>
              <input className={inputCls} value={funcSel} onChange={e => setFuncSel(e.target.value)} placeholder="12534" onKeyDown={e => { if (e.key === 'Enter') buscar(); }} />
            </div>
            <button onClick={buscar} className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-2 h-[38px]">
              <Search className="w-4 h-4" /> Buscar
            </button>
          </div>
        </div>
      )}

      {loading && <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando resultado...</div>}
      {erro && <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-700">{erro}</div>}

      {!loading && !erro && !res && (cicloSel || funcAtivo) && (
        <div className="bg-white rounded-2xl border border-gray-200 p-10 text-center text-gray-400">Nenhum resultado para exibir.</div>
      )}

      {res && (
        <>
          <div className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Resultado — {res.ciclo.nome}</div>
            <div className="flex items-end gap-4 flex-wrap mt-1">
              <div className="flex-1 min-w-0">
                <h2 className="text-xl font-bold text-gray-900">{res.avaliado.nome || `Colaborador #${res.avaliado.funccodi}`}</h2>
                <p className="text-sm text-gray-500">{res.avaliado.cargo || '—'} · {res.total_respostas} resposta(s)</p>
              </div>
              <div className="text-right">
                <div className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">Nota geral</div>
                <div className="text-3xl font-black text-purple-600">{res.nota_geral != null ? res.nota_geral.toFixed(1) : '—'}</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Radar por competência */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">Radar por competência</h3>
              {radarData.length === 0 ? (
                <p className="text-sm text-gray-400 p-6 text-center">Sem dados para o radar.</p>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <RadarChart data={radarData} outerRadius="70%">
                    <PolarGrid />
                    <PolarAngleAxis dataKey="competencia" tick={{ fontSize: 11 }} />
                    <PolarRadiusAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
                    {RADAR_SERIES.map(s => (
                      <Radar key={s.key} name={s.label} dataKey={s.key} stroke={s.cor} fill={s.cor} fillOpacity={0.15} />
                    ))}
                    <Legend />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Médias por tipo */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Médias por tipo</h3>
              <div className="grid grid-cols-2 gap-3">
                {(['auto', 'gestor', 'pares', 'liderados'] as const).map(tk => {
                  const v = res.por_tipo?.[tk];
                  return (
                    <div key={tk} className="rounded-xl border border-gray-100 p-3">
                      <div className="text-[11px] text-gray-400 uppercase tracking-wider">{TIPO_LABEL[tk]}</div>
                      <div className="text-2xl font-bold text-gray-900">{v && v.media != null ? v.media.toFixed(1) : '—'}</div>
                      <div className="text-[11px] text-gray-400">{v ? `${v.total} resp.` : 'sem dados'}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Tabela por competência */}
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Detalhe por competência</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2 text-left">Competência</th>
                  <th className="px-3 py-2 text-center">Média</th>
                  <th className="px-3 py-2 text-center">Auto</th>
                  <th className="px-3 py-2 text-center">Gestor</th>
                  <th className="px-3 py-2 text-center">Pares</th>
                  <th className="px-3 py-2 text-center">Liderados</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {res.por_competencia.map(c => {
                  const fmt = (n: number | null | undefined) => (n != null ? n.toFixed(1) : '—');
                  return (
                    <tr key={c.competencia_id}>
                      <td className="px-4 py-2 text-gray-800">
                        {c.nome}
                        {c.anonimato_insuficiente && c.media == null && (
                          <div className="text-[11px] text-amber-600 flex items-center gap-1 mt-0.5">
                            <ShieldAlert className="w-3.5 h-3.5" /> respostas insuficientes para preservar anonimato
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center font-semibold text-gray-900">{fmt(c.media)}</td>
                      <td className="px-3 py-2 text-center text-gray-600">{fmt(c.auto)}</td>
                      <td className="px-3 py-2 text-center text-gray-600">{fmt(c.gestor)}</td>
                      <td className="px-3 py-2 text-center text-gray-600">{fmt(c.pares)}</td>
                      <td className="px-3 py-2 text-center text-gray-600">{fmt(c.liderados)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Comentários (sem autor) */}
          {res.comentarios && res.comentarios.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2"><MessageSquareQuote className="w-4 h-4 text-gray-400" /> Comentários (anônimos)</h3>
              <ul className="space-y-2">
                {res.comentarios.map((c, i) => (
                  <li key={i} className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">
                    {c.competencia && <span className="block text-[11px] text-gray-400 mb-0.5">{c.competencia}</span>}
                    "{c.texto}"
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Resultado;
